// DS3.8
import React from 'react';
import numeral from 'numeral';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import TrashcanIcon from '@wix/da-ds/pkg/Icons/24x24/Trashcan';
import CloseIcon from '@wix/da-ds/pkg/Icons/24x24/Close';
import CheckIcon from '@wix/da-ds/pkg/Icons/24x24/Check';
import ErrorIcon from '@wix/da-ds/pkg/Icons/24x24/Error';
import IconButton from '../../../src/Button/IconButton';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import AttachedFile, { AttachedFileProps } from '@wix/da-ds/pkg/AttachedFile';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';

import s from './UploadedFile.scss';

export interface Props extends Pick<
  AttachedFileProps,
  'name' | 'size' | 'type' | 'width' | 'height' | 'previewUrl' | 'preview' | 'wrapLongNames'
> {
  uploadProgress?: number;
  className?: string;
  status: 'loading' | 'complete' | 'busy' | 'error';
  errorMessage?: string;
  rightSlot?: React.ReactElement;
  customName?: boolean;
  onRemove?: () => void;
}

const UploadedFile: React.FC<Props> = ({
  className,
  name,
  size,
  type,
  width,
  height,
  errorMessage,
  rightSlot,
  previewUrl,
  preview,
  wrapLongNames,
  status = 'loading',
  onRemove,
  uploadProgress,
  customName,
}) => {
  const { t } = useTranslation();
  const isLoading = status === 'loading';
  const isBusy = status === 'busy';
  const isComplete = status === 'complete';
  const hasError = status === 'error';

  return (
    <AttachedFile
      className={cn(s['root'], s[`status-${status}`], className)}
      name={name}
      customName={customName}
      size={size}
      type={type}
      width={width}
      height={height}
      previewUrl={previewUrl}
      preview={preview}
      wrapLongNames={wrapLongNames}
      middleSlot={
        hasError ? (
          <div className={s['error-message']}>
            {t('somethingWentWrong')}.{' '}
            {errorMessage && (
              <SimpleTooltip
                className={s['error-tooltip-handle']}
                contentClassName={s['error-tooltip']}
                text={errorMessage}
                usePortal
              >
                {t('common.learnMore')}
              </SimpleTooltip>
            )}
          </div>
        ) : null
      }
      rightSlot={
        <div className={s['remove-iconbutton-wrapper']}>
          {isLoading && (
            <div className={s['tools-wrapper']}>
              <span className={s['percentage']}>{numeral(uploadProgress || 0).format('0%')}</span>
              <IconButton
                className={s['llama-wrapper']}
                tooltipText={t('common.cancel')}
                onClick={onRemove}
              >
                <div className={s['llama-loader']} />
                <CloseIcon
                  className={s['remove-iconbutton']}
                  size={IconSize.SMALLER}
                  disableDefaultColors
                />
              </IconButton>
            </div>
          )}

          {isComplete && rightSlot}
          {isComplete && !rightSlot && onRemove && (
            <IconButton tooltipText={t('common.remove')} onClick={onRemove}>
              <TrashcanIcon
                className={s['remove-iconbutton']}
                size={IconSize.SMALLER}
                disableDefaultColors
              />
            </IconButton>
          )}
          {isComplete && !rightSlot && !onRemove && (
            <CheckIcon className={s['complete-icon']} size={IconSize.SMALL} disableDefaultColors />
          )}

          {isBusy && <div className={s['llama-loader']} />}

          {hasError && !rightSlot && (
            <ErrorIcon className={s['error-icon']} size={IconSize.SMALL} disableDefaultColors />
          )}
        </div>
      }
    />
  );
};
UploadedFile.displayName = 'UploadedFile';

export default UploadedFile;
